import {
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
} from "@mui/material";
import { useState, useMemo, useCallback } from "react";
import { AppDatePicker } from "../../../../components/datepickers";
import {
  convertToTimezone,
  DATE_FORMATS,
  reactDatePickerFilterPassedTime,
  formatDate,
} from "../../../../services/date-and-time.utils";
import { AppModalDialogBox } from "../../../../components/modals";
import { CalendarToday, AccessTime, Add, Delete } from "@mui/icons-material";
import { SaveAndCancelButton } from "../../../../components/buttons";

interface Slot {
  id: string;
  from: Date;
  to: Date;
  ambassadorTimezoneFrom: string;
  ambassadorTimezoneTo: string;
  vendorTimezoneFrom: string;
  vendorTimezoneTo: string;
  dateKey: string;
}

function createSlotFromTimeSlot(
  item: { from: string; to: string },
  index: number,
  invitationData: any
): Slot {
  const from = new Date(item.from);
  const to = new Date(item.to);
  const dateKey = formatDate(from, DATE_FORMATS.yyymmdd);

  return {
    id: `${index}-${item.from}`,
    from,
    to,
    ambassadorTimezoneFrom: convertToTimezone(
      from,
      invitationData?.ambassador?.timezone?.value,
      DATE_FORMATS.fullDateWithTime
    ),
    ambassadorTimezoneTo: convertToTimezone(
      to,
      invitationData?.ambassador?.timezone?.value,
      DATE_FORMATS.hourMinWithAMPM
    ),
    vendorTimezoneFrom: convertToTimezone(
      from,
      invitationData?.client?.timezone?.value,
      DATE_FORMATS.fullDateWithTime
    ),
    vendorTimezoneTo: convertToTimezone(
      to,
      invitationData?.client?.timezone?.value,
      DATE_FORMATS.hourMinWithAMPM
    ),
    dateKey,
  };
}

function EditVendorSlotsModal({
  handleModalClose,
  invitationData,
  onSubmit,
}: any) {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [slots, setSlots] = useState<Slot[]>(() =>
    (invitationData?.availableTimeSlots || []).map((item, index) =>
      createSlotFromTimeSlot(item, index, invitationData)
    )
  );

  const handleDateChange = useCallback((date: Date | null) => {
    setSelectedDate(date);
  }, []);

  const handleAddSlot = useCallback(() => {
    if (!selectedDate) return;

    const to = new Date(selectedDate.getTime() + 45 * 60000);
    const dateKey = formatDate(selectedDate, DATE_FORMATS.yyymmdd);

    const newSlot: Slot = {
      id: `${Date.now()}-${Math.random()}`,
      from: new Date(selectedDate),
      to,
      ambassadorTimezoneFrom: convertToTimezone(
        selectedDate,
        invitationData?.ambassador?.timezone?.value,
        DATE_FORMATS.fullDateWithTime
      ),
      ambassadorTimezoneTo: convertToTimezone(
        to,
        invitationData?.ambassador?.timezone?.value,
        DATE_FORMATS.hourMinWithAMPM
      ),
      vendorTimezoneFrom: convertToTimezone(
        selectedDate,
        invitationData?.client?.timezone?.value,
        DATE_FORMATS.fullDateWithTime
      ),
      vendorTimezoneTo: convertToTimezone(
        to,
        invitationData?.client?.timezone?.value,
        DATE_FORMATS.hourMinWithAMPM
      ),
      dateKey,
    };

    setSlots((prevSlots) => [...prevSlots, newSlot]);
    setSelectedDate(null);
  }, [selectedDate, invitationData]);

  const handleRemoveSlot = useCallback((slotId: string) => {
    setSlots((prevSlots) => prevSlots.filter((slot) => slot.id !== slotId));
  }, []);

  const { groupedSlots, sortedDates } = useMemo(() => {
    const grouped: { [key: string]: Slot[] } = {};
    slots.forEach((slot) => {
      if (!grouped[slot.dateKey]) {
        grouped[slot.dateKey] = [];
      }
      grouped[slot.dateKey].push(slot);
    });
    const sorted = Object.keys(grouped).sort();
    return { groupedSlots: grouped, sortedDates: sorted };
  }, [slots]);

  const handleSubmit = useCallback(() => {
    if (slots.length === 0) return;

    onSubmit({
      slots: slots.map((slot) => ({
        from: slot.from,
        to: slot.to,
      })),
    });
  }, [slots, onSubmit]);

  return (
    <AppModalDialogBox
      isModalOpen={true}
      title="Edit Vendor Proposed Times"
      handleClose={handleModalClose}
      size={"md"}
      actionButtonRenderer={
        <SaveAndCancelButton
          primaryText={"Save"}
          defaultText={"Cancel"}
          onPrimaryActionClick={handleSubmit}
          onDefaultButtonClick={handleModalClose}
          isPrimaryButtonDisabled={slots.length === 0}
        />
      }
    >
      <Box sx={{ p: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            border: "1px solid #e0e0e0",
            backgroundColor: "#fafafa",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <CalendarToday sx={{ mr: 1.5, color: "primary.main" }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                fontSize: 16,
              }}
            >
              Select Date & Time
            </Typography>
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <AppDatePicker
                dateFormat={"Pp"}
                showTimeSelect
                selectedDate={selectedDate}
                setDate={handleDateChange}
                minDate={new Date()}
                filterTime={reactDatePickerFilterPassedTime}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddSlot}
                disabled={!selectedDate}
                disableRipple
                disableElevation
                sx={{
                  height: 40,
                  transition: "none",
                  "&:hover": {
                    backgroundColor: "primary.main",
                    transition: "none",
                  },
                  "&:active": {
                    backgroundColor: "primary.main",
                    transition: "none",
                  },
                  "&:focus": {
                    backgroundColor: "primary.main",
                    transition: "none",
                  },
                }}
              >
                Add Time
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {slots.length > 0 && (
          <Box>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: "text.primary",
                display: "flex",
                alignItems: "center",
                fontSize: 16,
              }}
            >
              <AccessTime sx={{ mr: 1.5, color: "primary.main" }} />
              Selected Times
            </Typography>

            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                maxHeight: 400,
                overflow: "auto",
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: "#f5f5f5",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                        width: 52,
                        px: 1,
                      }}
                    >
                      Date
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: "#f5f5f5",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      Time
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: "#f5f5f5",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      Ambassador Timezone
                      <Typography
                        variant="caption"
                        display="block"
                        fontSize={11}
                        color="text.secondary"
                        fontWeight={400}
                      >
                        {invitationData?.ambassador?.timezone?.text ||
                          invitationData?.ambassador?.timezone?.value}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: "#f5f5f5",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      Vendor Timezone
                      <Typography
                        variant="caption"
                        display="block"
                        fontSize={11}
                        color="text.secondary"
                        fontWeight={400}
                      >
                        {invitationData?.client?.timezone?.text ||
                          invitationData?.client?.timezone?.value}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: "#f5f5f5",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                        width: 80,
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedDates.map((dateKey) => {
                    const dateSlots = groupedSlots[dateKey];

                    return dateSlots.map((slot, index) => (
                      <TableRow key={slot.id}>
                        {index === 0 && (
                          <TableCell
                            rowSpan={dateSlots.length}
                            sx={{
                              verticalAlign: "top",
                              borderRight: "1px solid #e0e0e0",
                              width: 52,
                              px: 1,
                              py: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 40,
                                py: 0.75,
                                px: 0.5,
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor: "primary.light",
                                backgroundColor: "#EFF6FF",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  lineHeight: 1,
                                  color: "primary.main",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.4,
                                }}
                              >
                                {formatDate(dateKey, "MMM")}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 18,
                                  fontWeight: 700,
                                  lineHeight: 1.1,
                                  color: "text.primary",
                                  my: 0.25,
                                }}
                              >
                                {formatDate(dateKey, "D")}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 9,
                                  fontWeight: 500,
                                  lineHeight: 1,
                                  color: "text.secondary",
                                }}
                              >
                                {formatDate(dateKey, "YYYY")}
                              </Typography>
                            </Box>
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontSize={13}
                            fontWeight={500}
                          >
                            {formatDate(slot.from, DATE_FORMATS.hourMinWithAMPM)}{" "}
                            -{" "}
                            {formatDate(slot.to, DATE_FORMATS.hourMinWithAMPM)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontSize={13}>
                            {slot.ambassadorTimezoneFrom} -{" "}
                            {slot.ambassadorTimezoneTo}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontSize={13}>
                            {slot.vendorTimezoneFrom} - {slot.vendorTimezoneTo}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveSlot(slot.id)}
                            disableRipple
                            sx={{
                              p: 0.5,
                              transition: "none",
                              "&:hover": {
                                backgroundColor: "transparent",
                                transition: "none",
                              },
                              "&:active": {
                                backgroundColor: "transparent",
                                transition: "none",
                              },
                              "&:focus": {
                                backgroundColor: "transparent",
                                transition: "none",
                              },
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ));
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>
    </AppModalDialogBox>
  );
}

export { EditVendorSlotsModal };
